import ContentSaveIcon from '@mui/icons-material/Save';
import { Button as MuiButton, Stack } from '@mui/material';
import { FormEvent, useEffect, useMemo } from 'react';
import {
    RecordContextProvider,
    Toolbar,
    useCreate,
    useDelete,
    useNotify,
    useUpdate,
} from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import {
    AnimationInput,
    animationSummaryToInput,
} from '@domain/animation/animation';
import { resourceInputPayload } from '@domain/types';
import type { AnimationSummary } from '@wwf/sdk';
import { AnimationInputs } from './AnimationInputs';

type DrawerMode = 'create' | 'edit' | 'show';

const defaultValues: AnimationInput = {
    name: '',
    description: '',
    documentLink: null,
    enabled: true,
    noSpecificMaterialRequired: false,
    theme: null,
    duration: null,
    ageTarget: null,
    missionType: null,
};

function toEditionPayload(data: AnimationInput): AnimationInput {
    return {
        name: data.name,
        description: data.description ?? null,
        documentLink: data.documentLink ? data.documentLink : null,
        enabled: data.enabled ?? true,
        noSpecificMaterialRequired: data.noSpecificMaterialRequired ?? false,
        theme: data.theme ?? null,
        duration: data.duration ?? null,
        ageTarget: data.ageTarget ?? null,
        missionType: data.missionType ?? null,
        ...resourceInputPayload(
            { inputKey: 'imageInput', idKey: 'imageId' },
            data.imageInput,
            data.imageId
        ),
    };
}

export function AnimationDrawerForm({
    mode,
    record,
    loading,
    onClose,
    onSuccess,
}: {
    mode: DrawerMode;
    record?: AnimationSummary;
    loading: boolean;
    onClose(): void;
    onSuccess(): void;
}) {
    const notify = useNotify();
    const [create] = useCreate();
    const [update] = useUpdate();
    const [deleteOne] = useDelete();

    const form = useForm<AnimationInput>({
        defaultValues,
    });

    const formRecord = useMemo(
        () =>
            record && (mode === 'edit' || mode === 'show')
                ? animationSummaryToInput(record)
                : undefined,
        [record, mode]
    );

    useEffect(() => {
        if (formRecord) {
            form.reset(formRecord);
        } else {
            form.reset(defaultValues);
        }
    }, [formRecord, form]);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (mode === 'show') return;
        form.handleSubmit(async (data: AnimationInput) => {
            try {
                const editionData = toEditionPayload(data);

                if (mode === 'create') {
                    await create(
                        'animation',
                        {
                            data: editionData,
                            meta: {},
                        },
                        {
                            onSuccess: () => {
                                onSuccess();
                                onClose();
                            },
                        }
                    );
                } else {
                    if (!record?.id) {
                        notify('Record not found', { type: 'error' });
                        return;
                    }
                    await update(
                        'animation',
                        {
                            id: record.id,
                            data: editionData,
                            previousData: record as any,
                            meta: {},
                        },
                        {
                            onSuccess: () => {
                                onSuccess();
                                onClose();
                            },
                        }
                    );
                }
            } catch (e) {
                notify(String(e), { type: 'error' });
            }
        })(event);
    };

    const handleDelete = () => {
        if (!record?.id) return;
        deleteOne(
            'animation',
            { id: record.id, previousData: record as any },
            {
                onSuccess: () => {
                    onSuccess();
                    onClose();
                },
            }
        );
    };

    const saving = form.formState.isSubmitting;
    const canSave = !loading && !saving;

    return (
        <FormProvider {...form}>
            <RecordContextProvider value={formRecord ?? undefined}>
                <form onSubmit={handleSubmit}>
                    <Stack gap={2}>
                        <AnimationInputs readOnly={mode === 'show'} />
                    </Stack>
                    {mode !== 'show' && (
                        <Toolbar
                            sx={{
                                mt: 2,
                                px: 0,
                                justifyContent: 'space-between',
                            }}
                        >
                            <Stack direction="row" gap={1}>
                                <MuiButton
                                    variant="contained"
                                    type="submit"
                                    disabled={!canSave}
                                    startIcon={<ContentSaveIcon />}
                                >
                                    Save
                                </MuiButton>
                                <MuiButton
                                    variant="text"
                                    onClick={onClose}
                                    disabled={saving}
                                >
                                    Cancel
                                </MuiButton>
                            </Stack>
                            {mode === 'edit' && record?.id && (
                                <MuiButton
                                    variant="text"
                                    color="error"
                                    disabled={saving}
                                    onClick={() => {
                                        if (
                                            window.confirm(
                                                'Are you sure you want to delete this animation?'
                                            )
                                        ) {
                                            handleDelete();
                                        }
                                    }}
                                >
                                    Delete
                                </MuiButton>
                            )}
                        </Toolbar>
                    )}
                </form>
            </RecordContextProvider>
        </FormProvider>
    );
}
